// The channel panel's small view-model helpers: naming a conversation the way the sidebar does,
// and the glyph/label for its kind. Pure; everything with a rule lives in src/core.
import { displayName } from '../../../core/channelListDerivation';
import type { ChannelRecord, UserRecord } from '../../../core/records';
import type { IconName } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import type { ChannelPanelSnapshot } from '../../../data/features/channelPanel';

export type PanelTab = 'info' | 'members' | 'pinned' | 'files';

export function usersById(users: readonly UserRecord[]): Map<string, UserRecord> {
  return new Map(users.map((u) => [u.id, u]));
}

/** The conversation's name under the reader's policy — the same derivation as its sidebar row. */
export function conversationName(snap: ChannelPanelSnapshot): string {
  const channel = snap.channel;
  if (!channel) return '';
  return displayName(channel, {
    currentUserId: snap.currentUserId ?? '',
    usersById: usersById(snap.users),
    participantIds: snap.participantIds,
    unknownUserFallback: t('channel.reaction.unknown'),
  });
}

export function typeIcon(type: string): IconName {
  switch (type) {
    case 'P':
      return 'lock';
    case 'D':
      return 'person';
    case 'G':
      return 'people';
    default:
      return 'hash';
  }
}

export function typeLabel(channel: ChannelRecord): string {
  if (channel.deleteAt) return t('web.channelPanel.archived');
  switch (channel.type) {
    case 'P':
      return t('web.channelPanel.privateChannel');
    case 'D':
      return t('web.channelPanel.directMessage');
    case 'G':
      return t('web.channelPanel.groupMessage');
    default:
      return t('web.channelPanel.publicChannel');
  }
}

/** The tabs a conversation offers: a DM has no roster worth a tab (Swift hides "Miembros" for D). */
export function tabsFor(type: string | undefined): PanelTab[] {
  return type === 'D' ? ['info', 'pinned', 'files'] : ['info', 'members', 'pinned', 'files'];
}
